import React, { useEffect, useState } from "react";
import useDeviceType from "../../../../custom-hooks/DeviceType";
import { VALIDATION_CONSTANTS } from "../../../../utils/validation-constants/validationConstants";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Button from "../../../../component-lib/Molecules/button/Button";
import { useDispatch, useSelector } from "react-redux";
import { EDIT_PROVIDER_PROFILE_DETAILS, TYPE_OF_GENDER } from "./constants";
import Input from "../../../../component-lib/Atoms/Input/Input";
import SelectDropdown from "../../../../component-lib/Atoms/Select/Select";
import ContactInput from "../../../../component-lib/Atoms/Input/ContactInput";
import { normalizeContactNumber } from "../../../../utils/phoneNumberFormatter";
import { TEXT_CONSTANTS } from "../../../../utils/text-constants/textConstants";
import Heading from "../../../../component-lib/Atoms/heading/EventEHRHeading";
import { pencil } from "../../../../assets";
import {
  componentKey,
  setCloseAdminUserProfileData,
} from "../../SettingsSlice";
import { getProfileData, patchProfileData } from "../../SettingsSaga";
import { getLoginUserId } from "../../../../utils/getToken";

interface ViewMyAccountDialogProps {
  //patientDetails?: PatientDetail[];
  isOpen: boolean;
  defaultFieldsData?: any;
  onEditClick?: () => void; // Callback function
  handleOnSubmittedData?: (formData: any) => void;
  isEditable?: boolean;
  handleClose: () => void;
}

const ViewMyAccountDialog = ({
  defaultFieldsData,
  onEditClick,
  isOpen = false,
  handleClose,
  handleOnSubmittedData,
  isEditable,
}: ViewMyAccountDialogProps) => {
  const { closeAdminUserProfile, adminUserProfileData } = useSelector(
    (state: any) => state[componentKey]
  );

  const deviceType = useDeviceType();

  const parseLoginUserId: any = getLoginUserId();
  const loginUserId = JSON.parse(parseLoginUserId);

  const dispatch = useDispatch();
  const [isEdit, setIsEdit] = useState(false);

  React.useEffect(() => {
    getAdminUser();
  }, []);

  useEffect(() => {
    if (closeAdminUserProfile) {
      dispatch(setCloseAdminUserProfileData(false));
      setIsEdit(false);
      getAdminUser();
    }
  }, [closeAdminUserProfile]);

  const getAdminUser = () => {
    const payload = {
      id: loginUserId,
    };
    dispatch(getProfileData(payload));
  };

  const handleIsEdited = () => {
    setIsEdit(!isEdit);
  };

  const handleUpdateProfile = (data: any) => {
    const apiPayload = {
      payload: {
        firstName: data?.firstName,
        lastName: data?.lastName,
        gender: data?.gender,
        contactNumber: data?.userNumber,
      },
      id: loginUserId,
    };

    dispatch(patchProfileData(apiPayload));
  };

  const gender = adminUserProfileData?.gender;
  const formattedData = gender
    ? gender.charAt(0).toUpperCase() + gender.substring(1)
    : "-";

  const userDetails = [
    { label: "BSA ID", value: adminUserProfileData.bsaId },
    { label: "First Name", value: adminUserProfileData.firstName },
    { label: "Last Name", value: adminUserProfileData.lastName },
    { label: "Contact Number", value: adminUserProfileData.contactNumber },
    { label: "Email", value: adminUserProfileData.email },
    { label: "Gender", value: formattedData ?? "-" },
    { label: "Status", value: adminUserProfileData.status },
    { label: "Role", value: adminUserProfileData.type },
  ];

  const validationSchema = Yup.object().shape({
    [EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.FIRST_LAST_NAME,
        "First name can only contain characters"
      )
      .required("First name is required"),
    [EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.FIRST_LAST_NAME,
        "Last name can only contain characters"
      )
      .required("Last name is required"),
    [EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER]: Yup.string()
      .matches(VALIDATION_CONSTANTS.CONTACT_NUMBER, "Invalid contact number")
      .required("Contact number is required"),

    [EDIT_PROVIDER_PROFILE_DETAILS.GENDER]:
      Yup.string().required("Gender is required"),
    [EDIT_PROVIDER_PROFILE_DETAILS.ROLE]:
      Yup.string().required("Role is required"),
    [EDIT_PROVIDER_PROFILE_DETAILS.STATUS]:
      Yup.string().required("Status is required"),
  });

  const commonButtonStyle = {
    width: "120px",
    height: "44px",
    padding: "12px 20px",
    fontSize: "16px",
  };

  const commonGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
    gap: "24px 18px",
  };

  // ADJUSTED GRID MEANS MADE SCREEN RESPONSIVE
  const adjustedGrid =
    deviceType === "tablet" || deviceType === "small-tablet"
      ? "1fr 1fr"
      : deviceType === "mobile"
        ? "1fr"
        : "1fr 1fr 1fr 1fr";
  const adjustedWidth =
    deviceType === "tablet" ||
    deviceType === "small-tablet" ||
    deviceType === "mobile";

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div
        className="flex flex-col bg-white border border-gray-300 rounded-[4px] shadow-lg p-3 max-w-5xl w-[90%] pl-3"
        style={{ gap: "24px" }}
      >
        <div className="flex">
          <div className="flex flex-col">
            <div className="p-2">
              <Heading
                title={isEdit ? "Edit Account Details" : "Account Details"}
              />
            </div>
          </div>

          <div className="ml-auto">
            {!isEdit && (
              <Button
                startIcon={
                  <img
                    src={pencil}
                    alt={"edit"}
                    className="cursor-pointer ml-[10px]"
                  />
                }
                //label="Edit Profile"
                variant="outlined"
                style={{
                  padding: "5px 15px",
                  background: "#FFFFFF",
                  color: "#364144",
                  border: "1px solid #D2D2D2",
                  width: "max-content",
                  visibility: !isEditable ? "visible" : "hidden",
                }}
                onClickCb={() => {
                  handleIsEdited();
                }}
              />
            )}
          </div>
        </div>

        {!isEdit ? (
          <div
            className="patient-details-card"
            style={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: adjustedGrid,
              gap: "18px",
              fontSize: "100%",
              position: "relative",
              padding: "7px",
              marginBottom: "10px",
            }}
          >
            {userDetails?.map((data, index) => (
              <div key={index} className="flex flex-col gap-[7px]">
                <div key={index} className="flex flex-col gap-2">
                  <div className="text-md font-medium text-gray-500">
                    {data?.label}
                  </div>
                  <div className="text-gray-800 font-medium">{data?.value}</div>
                </div>
              </div>
            ))}
            <div
              style={{
                gridColumn: "1 / -1",
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "5px",
              }}
            >
              <Button
                label={TEXT_CONSTANTS?.CLOSE}
                variant="contained"
                onClickCb={handleClose}
                style={{
                  ...commonButtonStyle,
                }}
              ></Button>
            </div>
          </div>
        ) : (
          <div className="w-full p-[6px]">
            <Formik
              initialValues={{
                [EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID]: `${adminUserProfileData?.bsaId || "-"}`,
                [EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME]: `${adminUserProfileData?.firstName || "-"}`,
                [EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME]: `${adminUserProfileData?.lastName || "-"}`,
                // [EDIT_PROVIDER_PROFILE_DETAILS.FULL_NAME]: `${fullName || "-"}`,
                [EDIT_PROVIDER_PROFILE_DETAILS.EMAIL]: `${adminUserProfileData?.email || "-"}`,
                [EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER]:
                  normalizeContactNumber(adminUserProfileData?.contactNumber),
                [EDIT_PROVIDER_PROFILE_DETAILS.GENDER]: `${adminUserProfileData?.gender?.trim()}`,
                [EDIT_PROVIDER_PROFILE_DETAILS.ROLE]: `${adminUserProfileData?.type || "-"}`,
                [EDIT_PROVIDER_PROFILE_DETAILS.STATUS]: `${adminUserProfileData?.status || "-"}`,
              }}
              validationSchema={validationSchema}
              enableReinitialize
              onSubmit={(values, { setSubmitting }) => {
                try {
                  //handleOnSubmittedData && handleOnSubmittedData(values);
                  handleUpdateProfile(values);
                  // Handle form data here
                  // If needed, call an API or further process the values here
                } catch (error) {
                  console.log(error);
                } finally {
                  setSubmitting(false); // Reset submitting state
                }
              }}
            >
              {({
                errors,
                submitForm,
                isSubmitting,
                handleReset,
                isValid,
                values,
                touched,
                setFieldValue,
                handleBlur,
                handleSubmit,
              }: any) => {
                return (
                  <Form
                    onSubmit={handleSubmit}
                    className="h-full flex flex-col justify-between rounded-lg"
                  >
                    <div className="w-full flex flex-col rounded-lg p-2 mb-5 ">
                      {/* PATIENT DETAILS */}
                      <div>
                        <div style={commonGridStyle}>
                          <Input
                            disabled={isEdit}
                            textFieldLabel="BSA ID"
                            name={EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID}
                            value={values[EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID]}
                            onChangeCb={(e) => {
                              setFieldValue(
                                EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID,
                                e.target.value
                              );
                            }}
                            onBlur={handleBlur}
                            size="small"
                            error={
                              touched[EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID] &&
                              Boolean(
                                errors[EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID]
                              )
                            }
                            helperText={
                              touched[EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID]
                                ? errors[EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID]
                                : undefined
                            }
                          />

                          <Input
                            textFieldLabel="First Name"
                            name={EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME}
                            value={
                              values[EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME]
                            }
                            onChangeCb={(e) => {
                              setFieldValue(
                                EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME,
                                e.target.value
                              );
                            }}
                            onBlur={handleBlur}
                            size="small"
                            error={
                              touched[
                                EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME
                              ] &&
                              Boolean(
                                errors[EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME]
                              )
                            }
                            helperText={
                              touched[EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME]
                                ? errors[
                                    EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME
                                  ]
                                : undefined
                            }
                          />

                          <Input
                            textFieldLabel="Last Name"
                            name={EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME}
                            value={
                              values[EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME]
                            }
                            onChangeCb={(e) => {
                              setFieldValue(
                                EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME,
                                e.target.value
                              );
                            }}
                            onBlur={handleBlur}
                            size="small"
                            error={
                              touched[
                                EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME
                              ] &&
                              Boolean(
                                errors[EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME]
                              )
                            }
                            helperText={
                              touched[EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME]
                                ? errors[
                                    EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME
                                  ]
                                : undefined
                            }
                          />

                          <Input
                            disabled={isEdit}
                            textFieldLabel="Email"
                            name={EDIT_PROVIDER_PROFILE_DETAILS.EMAIL}
                            value={values[EDIT_PROVIDER_PROFILE_DETAILS.EMAIL]}
                            onChangeCb={(e) => {
                              setFieldValue(
                                EDIT_PROVIDER_PROFILE_DETAILS.EMAIL,
                                e.target.value
                              );
                            }}
                            onBlur={handleBlur}
                            size="small"
                            error={
                              touched[EDIT_PROVIDER_PROFILE_DETAILS.EMAIL] &&
                              Boolean(
                                errors[EDIT_PROVIDER_PROFILE_DETAILS.EMAIL]
                              )
                            }
                            helperText={
                              touched[EDIT_PROVIDER_PROFILE_DETAILS.EMAIL]
                                ? errors[EDIT_PROVIDER_PROFILE_DETAILS.EMAIL]
                                : undefined
                            }
                          />

                          <SelectDropdown
                            label="Gender"
                            value={values[EDIT_PROVIDER_PROFILE_DETAILS.GENDER]}
                            options={TYPE_OF_GENDER}
                            onChangeCb={(e) =>
                              setFieldValue(
                                EDIT_PROVIDER_PROFILE_DETAILS.GENDER,
                                e.target.value
                              )
                            }
                            onBlur={handleBlur}
                            error={
                              touched[EDIT_PROVIDER_PROFILE_DETAILS.GENDER] &&
                              Boolean(
                                errors[EDIT_PROVIDER_PROFILE_DETAILS.GENDER]
                              )
                            }
                            helperText={
                              touched[EDIT_PROVIDER_PROFILE_DETAILS.GENDER]
                                ? errors[EDIT_PROVIDER_PROFILE_DETAILS.GENDER]
                                : undefined
                            }
                          />

                          <ContactInput
                            textFieldLabel="Phone"
                            name={EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER}
                            value={
                              values[
                                EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER
                              ]
                            }
                            onChangeCb={(value) => {
                              setFieldValue(
                                EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER,
                                value
                              );
                            }}
                            onBlur={handleBlur}
                            size="small"
                            error={
                              touched[
                                EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER
                              ] &&
                              Boolean(
                                errors[
                                  EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER
                                ]
                              )
                            }
                            helperText={
                              touched[
                                EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER
                              ]
                                ? errors[
                                    EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER
                                  ]
                                : undefined
                            }
                            isContactNumber={true}
                          />

                          <Input
                            disabled={isEdit}
                            textFieldLabel="Status"
                            name={EDIT_PROVIDER_PROFILE_DETAILS.STATUS}
                            value={values[EDIT_PROVIDER_PROFILE_DETAILS.STATUS]}
                            onChangeCb={(e) => {
                              setFieldValue(
                                EDIT_PROVIDER_PROFILE_DETAILS.STATUS,
                                e.target.value
                              );
                            }}
                            onBlur={handleBlur}
                            size="small"
                            error={
                              touched[EDIT_PROVIDER_PROFILE_DETAILS.STATUS] &&
                              Boolean(
                                errors[EDIT_PROVIDER_PROFILE_DETAILS.STATUS]
                              )
                            }
                            helperText={
                              touched[EDIT_PROVIDER_PROFILE_DETAILS.STATUS]
                                ? errors[EDIT_PROVIDER_PROFILE_DETAILS.STATUS]
                                : undefined
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end mt-4 gap-4">
                      <Button
                        label={TEXT_CONSTANTS?.CANCEL}
                        variant="outlined"
                        onClickCb={handleIsEdited}
                        style={{
                          ...commonButtonStyle,

                          backgroundColor: "white",
                          border: "1px solid #D2D2D2",
                          color: "#393939",
                        }}
                      />
                      <Button
                        type="submit"
                        label={TEXT_CONSTANTS?.SAVE}
                        variant="contained"
                        onClickCb={() => {}}
                        style={{
                          ...commonButtonStyle,
                        }}
                      ></Button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewMyAccountDialog;
